
import React, { useState } from 'react';
import { analyzeNightlyReview } from '../services/geminiService';
import { ReviewResponse, UserProfile, ReviewLog, DefectScores } from '../types';
import { Send, Loader2, Sparkles, AlertTriangle, History, TrendingUp, Calendar, ArrowRight, Share2, Check, BarChart3, ArrowLeft, FileText, Quote, BellRing, ShieldCheck, Lock } from 'lucide-react';

interface NightlyReviewProps {
  profile: UserProfile;
  onUpdate: (profile: UserProfile) => void;
  initialTab?: 'checkin' | 'history' | 'trends';
}

export const NightlyReview: React.FC<NightlyReviewProps> = ({ profile, onUpdate, initialTab = 'checkin' }) => {
  const [activeTab, setActiveTab] = useState<'checkin' | 'history' | 'trends'>(initialTab);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<ReviewResponse | null>(null);
  
  // State for viewing historical details
  const [selectedReview, setSelectedReview] = useState<ReviewLog | null>(null);

  const handleAnswerChange = (id: string, value: string) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setFeedback(null);
    
    const fullReview = profile.customQuestions.map(q => `Question: ${q.text}\nAnswer: ${answers[q.id] || "No answer provided."}`).join('\n\n');

    try {
      const result = await analyzeNightlyReview(fullReview);
      setFeedback(result);
      
      // Save to History
      const newEntry: ReviewLog = {
          id: Date.now().toString(),
          date: new Date().toISOString(),
          answers: answers,
          aiResponse: result
      };

      // Keep only last 30 entries for cleanliness in this demo
      const updatedHistory = [newEntry, ...(profile.reviewHistory || [])].slice(0, 30);
      onUpdate({ ...profile, reviewHistory: updatedHistory });

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const resetReview = () => {
    setFeedback(null);
    setAnswers({});
  };

  // --- Helper to get question text by ID ---
  const getQuestionText = (id: string) => {
      const q = profile.customQuestions.find(q => q.id === id);
      return q ? q.text : "Archived Question";
  };

  // --- Render Helpers ---

  const renderMainTrend = () => {
      const history = [...(profile.reviewHistory || [])].reverse();
      if (history.length < 2) return <div className="text-center p-8 text-slate-400">Not enough data for trends yet. Complete a few reviews!</div>;

      const maxScore = 10;
      
      return (
          <div className="mt-4">
              <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Overall Spiritual Condition</h4>
              <div className="flex items-end h-[100px] gap-1 border-b border-slate-200 dark:border-slate-700 pb-2">
                  {history.map((entry, idx) => {
                      const score = entry.aiResponse.sentimentScore || 5;
                      const heightPercent = (score / maxScore) * 100;
                      const colorClass = score >= 8 ? 'bg-green-400' : score >= 5 ? 'bg-amber-400' : 'bg-red-400';
                      
                      return (
                          <div key={entry.id} className="relative group flex-1 flex flex-col justify-end h-full">
                              <div 
                                  className={`w-full rounded-t-sm transition-all duration-500 hover:opacity-80 ${colorClass}`}
                                  style={{ height: `${heightPercent}%` }}
                              ></div>
                              {/* Tooltip */}
                              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                                  <div className="font-bold">{new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                                  <div>Score: {score}/10</div>
                              </div>
                          </div>
                      )
                  })}
              </div>
          </div>
      );
  };

  const renderSparkline = (label: string, dataKey: keyof DefectScores, color: string) => {
      const history = [...(profile.reviewHistory || [])].reverse();
      if (history.length === 0) return null;

      return (
          <div className="flex flex-col">
              <div className="flex justify-between items-end mb-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500 dark:text-slate-400">{label}</span>
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Last: {history[history.length - 1].aiResponse.defectScores?.[dataKey] ?? 0}
                  </span>
              </div>
              <div className="h-12 flex items-end gap-1">
                  {history.map((entry) => {
                      // Default to 0 if defectScores doesn't exist (old data)
                      const val = entry.aiResponse.defectScores?.[dataKey] ?? 0;
                      const height = (val / 10) * 100;
                      return (
                          <div 
                              key={entry.id} 
                              className={`flex-1 rounded-t-sm opacity-80 ${color}`} 
                              style={{ height: `${Math.max(height, 5)}%` }} // Min height 5% so 0 is visible as a line
                              title={`${label}: ${val}`}
                          />
                      )
                  })}
              </div>
          </div>
      )
  };

  // --- Feedback View (After Submission) ---
  if (feedback) {
    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-xl font-bold mb-6 dark:text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-500" /> Guidance from "The Fellow Traveler"
            </h3>
             <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          
          {feedback.isHighRisk && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg shadow-sm">
              <div className="flex items-start gap-3">
                 <ShieldCheck className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                 <div>
                    <h4 className="font-bold text-amber-800 dark:text-amber-300 mb-1">Support Protocol Activated</h4>
                    <p className="text-amber-700 dark:text-amber-200 text-sm mb-3 leading-relaxed">
                        It sounds like things are heavy right now. To support your sobriety, we have sent a secure, private ping to your sponsor asking them to call you.
                    </p>
                    
                    <div className="bg-white/60 dark:bg-black/20 border border-amber-200 dark:border-amber-800 rounded p-3 mb-2">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Message Sent:</p>
                        <p className="font-mono text-xs text-slate-700 dark:text-slate-300">
                            "{profile.firstName} {profile.lastInitial}. needs support, please call them soon."
                        </p>
                    </div>

                    <div className="flex items-center gap-1.5 text-[10px] text-amber-800/70 dark:text-amber-400">
                        <Lock className="w-3 h-3" />
                        <span>Your detailed review answers above were <b>not</b> shared.</span>
                    </div>
                 </div>
              </div>
            </div>
          )}
          
          <div className="grid gap-4 md:grid-cols-1">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-5 rounded-xl border border-indigo-100 dark:border-indigo-800">
              <h4 className="font-bold text-indigo-700 dark:text-indigo-300 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Insight from the Big Book
              </h4>
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed italic border-l-2 border-indigo-200 pl-3">
                  "{feedback.insight}"
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-xl border border-amber-100 dark:border-amber-800">
                <h4 className="font-bold text-amber-700 dark:text-amber-300 mb-2">Supportive Reflection</h4>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{feedback.reflection}</p>
                </div>
                <div className="bg-teal-50 dark:bg-teal-900/20 p-5 rounded-xl border border-teal-100 dark:border-teal-800">
                <h4 className="font-bold text-teal-700 dark:text-teal-300 mb-2">Suggested Action</h4>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{feedback.action}</p>
                </div>
            </div>
          </div>
          
          {/* Defect Analysis */}
          <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
               <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                   <BarChart3 className="w-4 h-4" /> Detected Defects (Intensity 0-10)
               </h4>
               <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                   {[
                       { l: 'Resentment', v: feedback.defectScores.resentment, c: 'bg-red-500' },
                       { l: 'Selfishness', v: feedback.defectScores.selfishness, c: 'bg-orange-500' },
                       { l: 'Dishonesty', v: feedback.defectScores.dishonesty, c: 'bg-yellow-500' },
                       { l: 'Fear', v: feedback.defectScores.fear, c: 'bg-purple-500' },
                   ].map((item) => (
                       <div key={item.l} className="space-y-1">
                           <div className="flex justify-between text-xs text-slate-500">
                               <span>{item.l}</span>
                               <span className="font-mono">{item.v}</span>
                           </div>
                           <div className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                               <div className={`h-full ${item.c}`} style={{ width: `${(item.v / 10) * 100}%` }}></div>
                           </div>
                       </div>
                   ))}
               </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-xs text-slate-400">
                Spiritual Condition Score: <b>{feedback.sentimentScore}/10</b>
            </div>
            <button
                onClick={resetReview}
                className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-white underline"
            >
                Return to Dashboard
            </button>
          </div>
        </div>
        </div>
    )
  }

  // --- Main Dashboard View ---
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Tab Header */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
            <button 
                onClick={() => { setActiveTab('checkin'); setSelectedReview(null); }}
                className={`flex-1 py-4 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'checkin' 
                    ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                }`}
            >
                <Sparkles className="w-4 h-4" /> Check-In
            </button>
            <button 
                onClick={() => { setActiveTab('trends'); setSelectedReview(null); }}
                className={`flex-1 py-4 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'trends' 
                    ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                }`}
            >
                <TrendingUp className="w-4 h-4" /> Trends
            </button>
            <button 
                onClick={() => { setActiveTab('history'); setSelectedReview(null); }}
                className={`flex-1 py-4 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'history' 
                    ? 'border-brand-600 text-brand-600 dark:text-brand-400' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                }`}
            >
                <History className="w-4 h-4" /> History
            </button>
        </div>

        <div className="p-6">
            {/* --- CHECK IN TAB --- */}
            {activeTab === 'checkin' && (
                <div>
                    <div className="mb-6">
                        <h3 className="text-xl font-bold dark:text-white">Step 11 Nightly Review</h3>
                        <p className="text-sm text-slate-500">Constructively review your day.</p>
                        {profile.sharingConfig?.shareReviews && (
                             <span className="inline-flex items-center gap-1 mt-2 text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-100">
                                 <Share2 className="w-3 h-3" /> Shared with Sponsor
                             </span>
                        )}
                    </div>

                    <div className="space-y-6">
                        {profile.customQuestions.map((q, idx) => (
                            <div key={q.id} className="space-y-2">
                                <label className="text-sm font-medium text-slate-800 dark:text-slate-200 block">
                                    <span className="text-slate-400 mr-2">{idx + 1}.</span>
                                    {q.text}
                                </label>
                                <textarea
                                    className="w-full p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white focus:ring-1 focus:ring-brand-500 focus:outline-none resize-none h-20 text-sm"
                                    placeholder="Type your answer..."
                                    value={answers[q.id] || ''}
                                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-700">
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold flex justify-center items-center gap-2 transition-colors disabled:opacity-50 shadow-lg shadow-indigo-500/20"
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            {loading ? 'Consulting The Fellow Traveler...' : 'Complete Review'}
                        </button>
                    </div>
                </div>
            )}

            {/* --- TRENDS TAB --- */}
            {activeTab === 'trends' && (
                <div>
                    <div className="mb-6 flex justify-between items-start">
                        <div>
                            <h3 className="text-xl font-bold dark:text-white">Spiritual Condition</h3>
                            <p className="text-sm text-slate-500">Tracking your daily serenity and defects.</p>
                        </div>
                    </div>
                    
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700 mb-6">
                        {renderMainTrend()}
                    </div>

                    <h4 className="font-bold text-slate-800 dark:text-white mb-4">Defect Tracking (Last 14 Entries)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm">
                            {renderSparkline('Resentment', 'resentment', 'bg-red-500')}
                        </div>
                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm">
                            {renderSparkline('Selfishness', 'selfishness', 'bg-orange-500')}
                        </div>
                         <div className="bg-white dark:bg-slate-800 p-4 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm">
                            {renderSparkline('Dishonesty', 'dishonesty', 'bg-yellow-500')}
                        </div>
                         <div className="bg-white dark:bg-slate-800 p-4 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm">
                            {renderSparkline('Fear', 'fear', 'bg-purple-500')}
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-4">
                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800/50">
                            <span className="block text-2xl font-bold text-green-700 dark:text-green-300">
                                {profile.reviewHistory?.length || 0}
                            </span>
                            <span className="text-xs text-green-600 dark:text-green-400">Total Reviews</span>
                        </div>
                        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/50">
                             <span className="block text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                                {profile.reviewHistory?.length ? (profile.reviewHistory.reduce((acc, curr) => acc + (curr.aiResponse.sentimentScore || 5), 0) / profile.reviewHistory.length).toFixed(1) : '-'}
                            </span>
                            <span className="text-xs text-indigo-600 dark:text-indigo-400">Avg. Condition</span>
                        </div>
                    </div>
                </div>
            )}

            {/* --- HISTORY TAB --- */}
            {activeTab === 'history' && (
                <div className="space-y-4">
                    {selectedReview ? (
                        // DETAILED VIEW
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                             <button 
                                onClick={() => setSelectedReview(null)}
                                className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400 transition-colors"
                             >
                                 <ArrowLeft className="w-4 h-4" /> Back to List
                             </button>

                             <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h3 className="text-2xl font-bold dark:text-white">
                                        {new Date(selectedReview.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                                    </h3>
                                    <p className="text-sm text-slate-500">Nightly Review Detail</p>
                                </div>
                                <div className={`px-3 py-1.5 rounded-full text-sm font-bold ${
                                    (selectedReview.aiResponse.sentimentScore || 5) >= 8 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                    (selectedReview.aiResponse.sentimentScore || 5) >= 5 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                }`}>
                                    Condition Score: {selectedReview.aiResponse.sentimentScore}/10
                                </div>
                             </div>

                             {/* AI SECTION */}
                             <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-6 border border-slate-200 dark:border-slate-700 mb-8">
                                <h4 className="font-bold text-lg dark:text-white mb-4 flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-indigo-500" /> Guidance from "The Fellow Traveler"
                                </h4>
                                
                                {selectedReview.aiResponse.isHighRisk && (
                                    <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg">
                                      <div className="flex items-start gap-3">
                                         <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                         <div>
                                            <p className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase">Support Protocol Activated</p>
                                            <p className="text-sm text-amber-700 dark:text-amber-200 mb-2">A private support request was sent to your sponsor.</p>
                                            <p className="text-[10px] text-slate-500 font-mono bg-white/50 dark:bg-black/20 p-1.5 rounded border border-amber-100 dark:border-amber-900 inline-block">
                                                Msg: "{profile.firstName} {profile.lastInitial}. needs support, please call them soon."
                                            </p>
                                         </div>
                                      </div>
                                    </div>
                                )}

                                <div className="grid gap-6 md:grid-cols-1">
                                    <div className="bg-white dark:bg-slate-800 p-5 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                        <h5 className="font-bold text-indigo-600 dark:text-indigo-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                                            <FileText className="w-3 h-3" /> Big Book Insight
                                        </h5>
                                        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed italic border-l-2 border-indigo-100 pl-3">"{selectedReview.aiResponse.insight}"</p>
                                    </div>
                                    
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="bg-white dark:bg-slate-800 p-5 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                            <h5 className="font-bold text-amber-600 dark:text-amber-400 text-xs uppercase tracking-wider mb-2">Reflection</h5>
                                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedReview.aiResponse.reflection}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 p-5 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                            <h5 className="font-bold text-teal-600 dark:text-teal-400 text-xs uppercase tracking-wider mb-2">Suggested Action</h5>
                                            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedReview.aiResponse.action}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Static Defect Bars for this entry */}
                                {selectedReview.aiResponse.defectScores && (
                                     <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                                         <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Defect Intensity</h5>
                                         <div className="grid grid-cols-4 gap-4">
                                            {(['resentment', 'selfishness', 'dishonesty', 'fear'] as const).map(k => {
                                                const val = selectedReview.aiResponse.defectScores?.[k] || 0;
                                                const color = k === 'resentment' ? 'bg-red-500' : k === 'selfishness' ? 'bg-orange-500' : k === 'dishonesty' ? 'bg-yellow-500' : 'bg-purple-500';
                                                return (
                                                    <div key={k} className="space-y-1">
                                                        <div className="flex justify-between text-[10px] text-slate-500 uppercase">
                                                            <span>{k}</span>
                                                            <span>{val}</span>
                                                        </div>
                                                        <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                            <div className={`h-full ${color}`} style={{ width: `${(val / 10) * 100}%` }}></div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                         </div>
                                     </div>
                                )}
                             </div>

                             {/* USER Q&A SECTION */}
                             <div>
                                <h4 className="font-bold text-lg dark:text-white mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-slate-500" /> Your Inventory
                                </h4>
                                <div className="space-y-4">
                                    {Object.entries(selectedReview.answers).map(([qId, answer]) => (
                                        <div key={qId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                                            <p className="font-medium text-slate-900 dark:text-slate-200 text-sm mb-1">
                                                {getQuestionText(qId)}
                                            </p>
                                            <div className="flex gap-3 text-slate-600 dark:text-slate-400 text-sm italic bg-slate-50 dark:bg-slate-900/30 p-3 rounded-lg">
                                                <Quote className="w-4 h-4 flex-shrink-0 opacity-50" />
                                                {answer}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                             </div>
                        </div>
                    ) : (
                        // LIST VIEW
                        <>
                            <div className="mb-4">
                                <h3 className="text-xl font-bold dark:text-white">Review History</h3>
                                <p className="text-sm text-slate-500">Click on an entry to view full details.</p>
                                {profile.sharingConfig?.shareReviews && (
                                    <p className="text-xs text-blue-500 flex items-center gap-1 mt-1">
                                        <Share2 className="w-3 h-3" /> Visible to Sponsor
                                    </p>
                                )}
                            </div>

                            {(!profile.reviewHistory || profile.reviewHistory.length === 0) && (
                                <div className="text-center py-12 text-slate-400">
                                    <History className="w-12 h-12 mx-auto mb-2 opacity-20" />
                                    <p>No history yet.</p>
                                </div>
                            )}

                            {profile.reviewHistory?.map((entry) => (
                                <div 
                                    key={entry.id} 
                                    onClick={() => setSelectedReview(entry)}
                                    className="group border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:border-brand-500 dark:hover:border-brand-500 hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
                                >
                                    {entry.aiResponse.isHighRisk && (
                                        <div className="absolute top-0 right-0 p-1 bg-amber-500 text-white rounded-bl-lg z-10" title="Support Requested">
                                            <ShieldCheck className="w-3 h-3" />
                                        </div>
                                    )}

                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-slate-400" />
                                            <span className="font-bold text-slate-700 dark:text-slate-200 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                                {new Date(entry.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                            </span>
                                            <ArrowRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 -ml-1 group-hover:ml-0 transition-all" />
                                        </div>
                                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                            (entry.aiResponse.sentimentScore || 5) >= 8 ? 'bg-green-100 text-green-700' :
                                            (entry.aiResponse.sentimentScore || 5) >= 5 ? 'bg-amber-100 text-amber-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            Score: {entry.aiResponse.sentimentScore || 5}
                                        </span>
                                    </div>
                                    
                                    <div className="space-y-2 pl-4 border-l-2 border-slate-100 dark:border-slate-700 group-hover:border-brand-200 dark:group-hover:border-brand-800 transition-colors">
                                        <div>
                                            <span className="text-[10px] uppercase tracking-wider text-slate-400">Insight</span>
                                            <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-1 italic">"{entry.aiResponse.insight}"</p>
                                        </div>
                                        
                                        {entry.aiResponse.defectScores && (
                                            <div className="flex gap-2 mt-2 pt-2 border-t border-dashed border-slate-200 dark:border-slate-700">
                                                {(['resentment', 'selfishness', 'dishonesty', 'fear'] as const).map(k => {
                                                    const val = entry.aiResponse.defectScores[k];
                                                    if (val > 0) {
                                                        return (
                                                            <span key={k} className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-500">
                                                                {k.charAt(0).toUpperCase() + k.slice(1)}: {val}
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    </div>
  );
};
